import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Target, MapPin, Building2, Layout, Bot, Sparkles, FileText, Share2, ArrowRight, Clock, Users, TrendingUp, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { API, DALLAS_SKYLINE, getIcon } from "@/lib/constants";

const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_e227742f-fa2d-44b4-a974-4045826f8a41/artifacts/epvouypm_DMN%20Logo%20Final%20A%20%28Light%29%20transparent.PNG";

// Process steps for each service type
const PROCESS_STEPS = {
  "local-seo-dallas": [
    { step: 1, title: "Discovery & Audit", desc: "We analyze your current local presence, competitors, and identify opportunities in the Dallas market." },
    { step: 2, title: "GBP Optimization", desc: "Complete optimization of your Google Business Profile including photos, posts, and Q&A." },
    { step: 3, title: "Citation Building", desc: "Build and clean up local citations across 50+ directories to ensure NAP consistency." },
    { step: 4, title: "Review Strategy", desc: "Implement review generation campaigns to build social proof and improve rankings." },
    { step: 5, title: "Local Content", desc: "Create geo-targeted content optimized for Dallas neighborhoods and surrounding areas." },
    { step: 6, title: "Monitor & Report", desc: "Track rankings, traffic, and leads with monthly reporting and strategy adjustments." }
  ],
  "google-business-profile-optimization": [
    { step: 1, title: "Profile Audit", desc: "Comprehensive review of your current GBP status, completeness, and optimization opportunities." },
    { step: 2, title: "Complete Setup", desc: "Fill in all profile sections, categories, attributes, and business information." },
    { step: 3, title: "Visual Optimization", desc: "Upload high-quality photos, videos, and virtual tours to showcase your business." },
    { step: 4, title: "Post Strategy", desc: "Develop and execute a weekly posting schedule with offers, updates, and events." },
    { step: 5, title: "Review Management", desc: "Implement review response templates and generation strategies." },
    { step: 6, title: "Performance Tracking", desc: "Monitor insights, track calls, and optimize based on data." }
  ],
  "ppc-advertising-dallas": [
    { step: 1, title: "Account Setup", desc: "Configure tracking, conversion goals, and campaign structure for optimal performance." },
    { step: 2, title: "Keyword Research", desc: "Identify high-intent keywords and negative keywords for your Dallas market." },
    { step: 3, title: "Ad Creation", desc: "Write compelling ad copy with strong CTAs and landing page alignment." },
    { step: 4, title: "Landing Pages", desc: "Design conversion-optimized landing pages for each campaign." },
    { step: 5, title: "Launch & Test", desc: "Launch campaigns with A/B testing for ads, audiences, and bidding strategies." },
    { step: 6, title: "Optimize & Scale", desc: "Continuous optimization based on data to improve ROAS and scale winners." }
  ],
  "web-design-dallas": [
    { step: 1, title: "Discovery Call", desc: "Understand your business goals, target audience, and design preferences." },
    { step: 2, title: "Wireframing", desc: "Create page layouts and user flow diagrams for your approval." },
    { step: 3, title: "Design Mockups", desc: "Develop visual designs with your brand colors, fonts, and imagery." },
    { step: 4, title: "Development", desc: "Build your site with clean code, SEO optimization, and mobile responsiveness." },
    { step: 5, title: "Content & SEO", desc: "Add optimized content, meta tags, schema markup, and images." },
    { step: 6, title: "Launch & Train", desc: "Go live with your site and provide training on managing your CMS." }
  ],
  "answer-engine-optimization": [
    { step: 1, title: "AI Visibility Audit", desc: "Analyze how your brand appears in ChatGPT, Perplexity, and Google AI Overviews." },
    { step: 2, title: "Content Gap Analysis", desc: "Identify questions your audience asks that AI could answer about you." },
    { step: 3, title: "Schema Implementation", desc: "Add FAQ, HowTo, and organization schema to help AI understand your content." },
    { step: 4, title: "Q&A Content Creation", desc: "Create authoritative content structured for AI consumption." },
    { step: 5, title: "Entity Building", desc: "Establish your brand as an entity AI platforms recognize and trust." },
    { step: 6, title: "Monitor AI Citations", desc: "Track when and how AI platforms mention your business." }
  ],
  "generative-engine-optimization": [
    { step: 1, title: "GEO Audit", desc: "Assess your current presence in generative AI search engines and citations." },
    { step: 2, title: "Authority Mapping", desc: "Identify authoritative sources where your business should be mentioned." },
    { step: 3, title: "Content Strategy", desc: "Develop comprehensive, factual content that AI platforms prefer to cite." },
    { step: 4, title: "E-E-A-T Enhancement", desc: "Build experience, expertise, authority, and trust signals across your presence." },
    { step: 5, title: "Citation Building", desc: "Get mentioned in publications, directories, and trusted sources AI learns from." },
    { step: 6, title: "Ongoing Optimization", desc: "Continuously improve based on AI search evolution and your citation tracking." }
  ],
  "content-marketing-dallas": [
    { step: 1, title: "Content Audit", desc: "Review existing content and identify gaps and opportunities." },
    { step: 2, title: "Strategy Development", desc: "Create a content calendar aligned with SEO goals and audience needs." },
    { step: 3, title: "Content Creation", desc: "Write high-quality, SEO-optimized blog posts, guides, and resources." },
    { step: 4, title: "Visual Assets", desc: "Design infographics, images, and videos to enhance content." },
    { step: 5, title: "Distribution", desc: "Promote content across social media, email, and outreach channels." },
    { step: 6, title: "Measure & Iterate", desc: "Track performance and refine strategy based on what resonates." }
  ],
  "social-media-marketing-dallas": [
    { step: 1, title: "Social Audit", desc: "Analyze current social presence, competitors, and audience demographics." },
    { step: 2, title: "Strategy Creation", desc: "Develop platform-specific strategies for your goals and audience." },
    { step: 3, title: "Content Calendar", desc: "Plan and schedule content that engages and converts." },
    { step: 4, title: "Community Management", desc: "Engage with followers, respond to comments, and build relationships." },
    { step: 5, title: "Paid Amplification", desc: "Boost top-performing content to reach new audiences." },
    { step: 6, title: "Analytics & Reporting", desc: "Track metrics and provide insights on growth and engagement." }
  ],
  "default": [
    { step: 1, title: "Discovery & Audit", desc: "We analyze your current presence, competitors, and biggest opportunities in your market." },
    { step: 2, title: "Custom Strategy", desc: "Build a roadmap tailored to your business goals, audience, and budget." },
    { step: 3, title: "Implementation", desc: "Execute the plan with clear milestones and full transparency at every step." },
    { step: 4, title: "Optimization", desc: "Test, measure, and refine continuously to improve performance." },
    { step: 5, title: "Scale What Works", desc: "Double down on the channels and campaigns driving the best return." },
    { step: 6, title: "Report & Iterate", desc: "Monthly reporting tied to leads and revenue — then we keep improving." }
  ]
};

// Map API service slugs to their matching process steps
const PROCESS_ALIASES = {
  "local-seo": "local-seo-dallas",
  "gbp-optimization": "google-business-profile-optimization",
  "ppc": "ppc-advertising-dallas",
  "web-design": "web-design-dallas",
  "aeo-geo": "answer-engine-optimization",
  "content-marketing": "content-marketing-dallas",
  "social-media": "social-media-marketing-dallas",
};

// Stats for credibility
const SERVICE_STATS = [
  { label: "Dallas–Fort Worth Focus", value: "Local", icon: Users },
  { label: "Traditional + AI Search", value: "SEO/AEO", icon: TrendingUp },
  { label: "Years Experience", value: "10+", icon: Clock },
  { label: "Google Rating", value: "5.0", icon: Award }
];

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await axios.get(`${API}/services/${slug}`);
        setService(response.data);
        document.title = response.data.meta_title || `${response.data.name || response.data.title} | Digital Media Ninja`;
      } catch (error) {
        console.error("Error fetching service:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-[#00E676] animate-pulse">Loading...</div>
      </div>
    );
  }
  
  if (!service) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-gray-400">Service not found</div>
      </div>
    );
  }

  const renderIcon = () => getIcon(service.icon, "w-16 h-16");

  const serviceTitle = service.name || service.title;
  const serviceDesc = service.long_description || service.description;
  const servicePricing = service.pricing_starts;
  const processSteps = PROCESS_STEPS[slug] || PROCESS_STEPS[PROCESS_ALIASES[slug]] || PROCESS_STEPS["default"];

  // Service Schema for AEO/GEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": serviceTitle,
    "description": serviceDesc,
    "provider": {
      "@type": "Organization",
      "name": "Digital Media Ninja",
      "@id": "https://digitalmedianinja.com/#organization"
    },
    "areaServed": {
      "@type": "City",
      "name": "Dallas"
    },
    "serviceType": serviceTitle
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "item": { "@id": "https://digitalmedianinja.com/", "name": "Home" } },
      { "@type": "ListItem", "position": 2, "item": { "@id": "https://digitalmedianinja.com/services/", "name": "Services" } },
      { "@type": "ListItem", "position": 3, "item": { "@id": "https://digitalmedianinja.com/services/" + slug, "name": serviceTitle } }
    ]
  };

  // FAQ Schema if service has FAQs
  const faqSchema = service.service_faqs && service.service_faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": service.service_faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="service-detail-page">
      <Helmet>
        <title>{service.meta_title || `${serviceTitle} | Digital Media Ninja Dallas`}</title>
        <meta name="description" content={service.meta_description || serviceDesc} />
        <link rel="canonical" href={`https://digitalmedianinja.com/services/${slug}`} />
        <meta property="og:title" content={serviceTitle} />
        <meta property="og:description" content={serviceDesc} />
        <meta property="og:image" content={DEFAULT_IMAGE} />
        <meta property="og:url" content={`https://digitalmedianinja.com/services/${slug}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={serviceTitle} />
        <meta name="twitter:description" content={serviceDesc} />
      </Helmet>

      {/* JSON-LD Schema Markup for AEO/GEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}

      {/* Hero Section */}
      <div className="container-custom mb-16">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <nav className="text-sm text-gray-500">
              <Link to="/" className="hover:text-[#00E676]">Home</Link>
              {" / "}
              <Link to="/services" className="hover:text-[#00E676]">Services</Link>
              {" / "}
              <span className="text-white">{serviceTitle}</span>
            </nav>
            <div className="text-[#00E676]">{renderIcon()}</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold">{serviceTitle}</h1>
            <p className="text-xl text-gray-400">{serviceDesc}</p>
            {servicePricing && (
              <p className="text-[#00E676] font-bold text-lg">Starting at {servicePricing}</p>
            )}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact"><Button className="btn-ninja">Get Free Consultation</Button></Link>
              <Button className="btn-outline">View Pricing</Button>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-white/10">
            <img src={DALLAS_SKYLINE} alt="Dallas" className="w-full h-80 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="border-y border-white/10 py-8 mb-16">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {SERVICE_STATS.map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-8 h-8 text-[#00E676] mx-auto mb-2" />
                <p className="text-3xl font-bold text-white">{stat.value}</p>
                <p className="text-gray-500 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-[#0A0A0A] py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold mb-4 text-center">What&apos;s Included</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Our {serviceTitle} package includes everything you need to dominate your market.
          </p>
          <ServiceFeatures features={service.features || []} />
        </div>
      </div>

      {/* Our Process Section */}
      <div className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold mb-4 text-center">Our Proven Process</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            We follow a systematic approach to deliver results for every Dallas business we work with.
          </p>
          <ProcessSteps steps={processSteps} />
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-[#0A0A0A] py-20">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <ServiceBenefits benefits={service.benefits} title={serviceTitle} />
            <div className="glass rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-6">Ready to Get Started?</h3>
              <p className="text-gray-400 mb-6">Get a free consultation and custom strategy for your Dallas business.</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-[#00E676]" />Free initial consultation
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-[#00E676]" />Custom strategy proposal
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-[#00E676]" />Transparent pricing
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                  <CheckCircle2 className="w-4 h-4 text-[#00E676]" />No long-term contracts
                </li>
              </ul>
              <Link to="/contact"><Button className="btn-ninja w-full">Get Your Free Audit</Button></Link>
            </div>
          </div>
        </div>
      </div>

      {/* Service FAQs */}
      {service.service_faqs && service.service_faqs.length > 0 && (
        <div className="py-20">
          <div className="container-custom">
            <h2 className="text-3xl font-bold mb-4 text-center">{serviceTitle} FAQs</h2>
            <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
              Common questions about our {serviceTitle} services.
            </p>
            <ServiceFAQs faqs={service.service_faqs} />
          </div>
        </div>
      )}

      {/* Related Services */}
      <div className="bg-[#0A0A0A] py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold mb-4 text-center">Related Services</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Explore other services that complement {serviceTitle}.
          </p>
          <RelatedServices currentSlug={slug} />
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-r from-[#00E676]/10 to-[#6366F1]/10">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Dominate Dallas with {serviceTitle}?</h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Join the Dallas businesses that trust Digital Media Ninja for their digital marketing success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact"><Button className="btn-ninja px-12">Get Your Free Consultation</Button></Link>
            <a href="tel:+14329349470"><Button className="btn-outline px-12">Call (432) 934-9470</Button></a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Component: Service Features Grid
const ServiceFeatures = ({ features }) => (
  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
    {features.map((feature, i) => (
      <div key={i} className="flex items-start gap-4 p-6 rounded-xl bg-white/5 border border-white/10 hover:border-[#00E676]/30 transition-all">
        <CheckCircle2 className="w-6 h-6 text-[#00E676] shrink-0 mt-1" />
        <span className="text-gray-300">{feature}</span>
      </div>
    ))}
  </div>
);

// Component: Process Steps
const ProcessSteps = ({ steps }) => (
  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
    {steps.map((step) => (
      <div key={step.step} className="relative">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#00E676]/20 flex items-center justify-center shrink-0 text-[#00E676] font-bold text-lg">
            {step.step}
          </div>
          <div>
            <h3 className="text-xl font-bold mb-2">{step.title}</h3>
            <p className="text-gray-400">{step.desc}</p>
          </div>
        </div>
        {step.step < steps.length && (
          <ArrowRight className="hidden lg:block absolute -right-4 top-4 w-8 h-8 text-white/20" />
        )}
      </div>
    ))}
  </div>
);

// Component: Service Benefits
const DEFAULT_BENEFITS = [
  "A strategy customized to your business, market, and growth goals",
  "Transparent monthly reporting tied to leads and revenue — not vanity metrics",
  "Search-first execution: technical foundations, content, and conversion working together",
  "No long-term contracts — we earn your business every month",
];

const ServiceBenefits = ({ benefits, title }) => (
  <div>
    <h2 className="text-3xl font-bold mb-8">Why Choose Our {title}?</h2>
    <div className="space-y-6">
      {(benefits && benefits.length ? benefits : DEFAULT_BENEFITS).map((benefit, i) => (
        <div key={i} className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-[#00E676]/20 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-5 h-5 text-[#00E676]" />
          </div>
          <p className="text-lg text-gray-200">{benefit}</p>
        </div>
      ))}
    </div>
  </div>
);

// Component: Service FAQs
const ServiceFAQs = ({ faqs }) => (
  <div className="max-w-3xl mx-auto">
    <Accordion type="single" collapsible className="space-y-4">
      {faqs.map((faq, i) => (
        <AccordionItem key={i} value={`faq-${i}`} className="border border-white/10 rounded-xl px-6 data-[state=open]:bg-white/5">
          <AccordionTrigger className="text-left font-medium hover:text-[#00E676] py-5">{faq.question}</AccordionTrigger>
          <AccordionContent className="text-gray-400 pb-5">{faq.answer}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </div>
);

// Component: Related Services
const RelatedServices = ({ currentSlug }) => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data.filter(s => s.slug !== currentSlug).slice(0, 3));
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, [currentSlug]);

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {services.map((service) => (
        <Card key={service.id} className="bg-black/50 border-white/10 hover:border-[#00E676]/30 transition-all">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-2 text-white">{service.name || service.title}</h3>
            <p className="text-gray-400 text-sm mb-4 line-clamp-2">{service.description}</p>
            <Link to={`/services/${service.slug}`}>
              <Button variant="ghost" className="text-[#00E676] hover:text-[#00C853] p-0">
                Learn More <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ServiceDetailPage;
